/**
 * ImagePicker Component
 *
 * Unified image input for admin forms. Provides three modes:
 * 1. Browse existing images from the media library
 * 2. Upload a new image
 * 3. Enter a URL directly
 *
 * Uploads automatically create hidden gallery items for reuse.
 */

import { useState, useRef } from 'react';
import {
  Upload,
  Link as LinkIcon,
  FolderOpen,
  X,
  Loader2,
  Check,
} from 'lucide-react';

import MediaLibraryBrowser from './MediaLibraryBrowser';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { uploads } from '@/services/api/admin';


/**
 * ImagePicker - Unified image input component
 *
 * @param {Object} props
 * @param {string} [props.value] - Current image URL
 * @param {Function} props.onChange - Callback with (url, galleryItemId?)
 * @param {string} [props.context] - Upload context for auto-tagging (e.g., "programs", "pages")
 * @param {string} [props.label] - Field label
 * @param {string} [props.placeholder] - URL input placeholder
 * @param {boolean} [props.allowClear] - Show clear button (default: true)
 * @param {string} [props.previewSize] - Preview size: 'sm', 'md', 'lg' (default: 'md')
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.disabled] - Disable all inputs
 */
export function ImagePicker({
  value,
  onChange,
  context = null,
  label = null,
  placeholder = 'Enter image URL...',
  allowClear = true,
  previewSize = 'md',
  className = '',
  disabled = false,
}) {
  const [mode, setMode] = useState('browse'); // 'browse' | 'upload' | 'url'
  const [showBrowser, setShowBrowser] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const previewSizes = {
    sm: 'h-16 w-24',
    md: 'h-24 w-36',
    lg: 'h-32 w-48',
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const result = await uploads.uploadImage(file, {
        autoGallery: true,
        context,
      });
      onChange(result.url, result.gallery_item_id);
    } catch (err) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUrlSubmit = () => {
    if (!urlInput.trim()) {
      setError('Please enter a URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(urlInput.trim());
    } catch {
      // Allow relative URLs
      if (!urlInput.startsWith('/')) {
        setError('Please enter a valid URL');
        return;
      }
    }

    onChange(urlInput.trim(), null);
    setUrlInput('');
    setShowUrlInput(false);
    setError('');
  };

  const handleBrowserSelect = (mediaItem) => {
    onChange(mediaItem.media_url, mediaItem.id);
    setShowBrowser(false);
  };

  const handleClear = () => {
    onChange('', null);
    setError('');
  };

  return (
    <div className={className}>
      {label && (
        <Label className="text-gray-800 dark:text-gray-200 font-medium mb-2 block">{label}</Label>
      )}

      {/* Current image preview */}
      {value && (
        <div className="relative inline-block mb-3">
          <div className={`${previewSizes[previewSize]} relative rounded-lg border-2 border-stone-200 overflow-hidden bg-stone-100`}>
            <img
              src={value}
              alt="Selected"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
          {allowClear && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute -top-2 -right-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-full p-1 hover:from-red-700 hover:to-red-800 shadow-md"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      )}

      {/* Action buttons */}
      {!disabled && (
        <div className="flex flex-wrap items-center gap-2">
          {/* Browse Library */}
          <Button
            type="button"
            variant="adminOutline"
            size="sm"
            onClick={() => setShowBrowser(true)}
          >
            <FolderOpen className="h-4 w-4 mr-1.5" />
            Browse
          </Button>

          {/* Upload */}
          <Button
            type="button"
            variant="adminOutline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-1.5" />
                Upload
              </>
            )}
          </Button>

          {/* URL Input */}
          <Popover open={showUrlInput} onOpenChange={setShowUrlInput}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="adminOutline"
                size="sm"
              >
                <LinkIcon className="h-4 w-4 mr-1.5" />
                URL
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="start">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Enter Image URL</Label>
                <div className="flex gap-2">
                  <Input
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder={placeholder}
                    className="flex-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleUrlSubmit();
                      }
                    }}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="adminPrimary"
                    onClick={handleUrlSubmit}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      )}

      {/* Current URL display */}
      {value && (
        <p className="text-xs text-stone-500 mt-2 truncate max-w-md" title={value}>
          {value}
        </p>
      )}

      {/* Error message */}
      {error && (
        <p className="text-red-500 text-sm mt-2">{error}</p>
      )}

      {/* Media Library Browser Modal */}
      <MediaLibraryBrowser
        open={showBrowser}
        onClose={() => setShowBrowser(false)}
        onSelect={handleBrowserSelect}
        context={context}
      />
    </div>
  );
}

export default ImagePicker;
